import React, {
  Dispatch,
  FormEventHandler,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Document as DocumentMsg,
  DOCUMENT_TYPE,
  AddDocumentRequest,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import isString from 'lodash/isString'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import utf8 from 'utf8'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import log from 'loglevel'
import { ThrobberContext } from 'src/Throbber/ThrobberContext'
import { Spinner } from 'src/Throbber'
import { ToastPrompts, showInfoToast } from 'src/Utils/helpers/toast'
import { P } from 'src/components/Reusable/Text/Text.styles'
import palette from 'src/designSystem/variables/palette'
import { StyledInputError } from 'src/components/Reusable/Inputs/TextInput/styles'
import { TextButton } from 'src/components/Reusable/Buttons/TextButton/TextButton'
import { useGetPolicyDocumentsByModelId } from 'src/pages/Policies/Policies.hooks'
import {
  useIsValidSharepointUrl,
  useSharepointLinkerShareUrl,
} from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerModal.hooks'
import { useSharepointFilePickerContext } from 'src/lib/Linkers/modals/SharePoint Picker/SharepointFilePickerContext'
import { MIME_TYPE } from 'src/Utils/globalEnums'
import { stripFileExtension } from 'src/components/Reusable/Forms/FileSelector/FileSelector.helpers'
import { useGoogleDriveContext } from 'src/lib/Linkers/context/GoogleDriveContext'
import {
  useAlertDialogModal,
  ShowAlertDialog,
} from 'src/components/ModalForms/useAlertDialogModal'
import { useConfluenceFilePickerContext } from 'src/lib/Linkers/modals/Confluence Picker/ConfluenceFilePickerContext'
import { DerivedTextInput } from '../../Reusable/Inputs'
import { ModalForm, ModalFormId, ModalFormIdQueryParam } from '../ModalForm'
import { useAuthorizedGrpcClientWithContentUpdate } from '../../../adapter/grpcClient'
import { usePolicy } from '../../async/model'
import { isValidUrl } from '../../../Utils/validators'
import { DocumentTypeSelector } from './DocumentTypeSelector'
import { DocumentFile } from './DocumentFile'
import { DocumentLink } from './DocumentLink'
import { DocumentDetailsContainer, Label } from './AddDocumentForm.styles'
import { PolicyDocumentManagementFiles } from './AddDocumentForm.components'

export type DocumentFormData = {
  mime: string
  caption: string
  body: string | File
}

export enum DocumentType {
  NULL = 'null',
  LINK = 'link',
  FILE = 'file',
  SHAREPOINTLINK = 'sharepointlink',
}

export const AddDocumentQueryParams = {
  MODAL_FORM_ID: ModalFormIdQueryParam,
  POLICY_ID: 'policyId',
}

type Props = {
  policyId: string
  openOnDrag?: boolean
}

const initialFormState: DocumentFormData = {
  mime: '',
  caption: '',
  body: '',
}

export const AddDocumentForm = ({
  policyId,
  openOnDrag = false,
}: Props): JSX.Element => {
  const showAlerDialog: ShowAlertDialog = useAlertDialogModal()
  const isSharepointurlValid = useIsValidSharepointUrl()
  const runSharepointLinker = useSharepointLinkerShareUrl()
  const {
    filesSelected: spFileSelected,
    setFilesSelected: setSpFilesSelected,
    onSubmit: onDMSubmit,
  } = useSharepointFilePickerContext()
  const {
    filesSelected: gdFileSelected,
    setFilesSelected: setGdFileSelected,
    onSubmit: onGdSubmit,
  } = useGoogleDriveContext()
  const {
    filesSelected: cfFileSelected,
    setFilesSelected: setCfFilesSelected,
    onSubmit: onCfSubmit,
  } = useConfluenceFilePickerContext()

  const policy = usePolicy(policyId)?.data
  const { mutate: mutatePolicyDocs } = useGetPolicyDocumentsByModelId(
    policy?.getModelId(),
    !!policy?.getModelId(),
  )
  const attachmentClient = useAuthorizedGrpcClientWithContentUpdate(
    AttachmentPromiseClient,
  )
  const [formData, formDataSet] = useState<DocumentFormData>(initialFormState)
  const [formErrors, setFormErrors] =
    useState<DocumentFormData>(initialFormState)
  const [type, typeSet] = useState<DocumentType>(DocumentType.NULL)
  const [isValidLink, setIsValidLink] = useState<boolean>(true)
  const { setThrobberState } = useContext(ThrobberContext)
  const hasSubmitted = useRef<boolean>(false)

  const hideCaption = useMemo(() => {
    return spFileSelected.length > 1 || (gdFileSelected || []).length > 1
  }, [spFileSelected, gdFileSelected])

  useEffect(() => {
    if (hasSubmitted.current && isString(formData.body)) {
      setIsValidLink(isValidUrl(formData.body))
    }
    if (formData.body) {
      setFormErrors((formErrors) => ({
        ...formErrors,
        body: '',
      }))
    }
  }, [formData.body, isValidLink])

  // Auto-set caption from selected SP / GD / CF entry
  useEffect(() => {
    if (spFileSelected.length === 1) {
      formDataSet({
        body: '',
        caption: stripFileExtension(spFileSelected[0].name),
        mime: MIME_TYPE.NULL,
      })
    } else if (gdFileSelected?.length === 1) {
      formDataSet({
        body: '',
        caption: stripFileExtension(gdFileSelected[0].name),
        mime: MIME_TYPE.NULL,
      })
    } else if (cfFileSelected.length === 1) {
      const node = cfFileSelected[0]
      const baseName = node.fileName || node.title || ''
      formDataSet({
        body: '',
        caption: stripFileExtension(baseName),
        mime: MIME_TYPE.NULL,
      })
    }
  }, [spFileSelected, gdFileSelected, cfFileSelected])

  const show = useIsShowModal(ModalFormId.ADD_DOCUMENT)
  const hide = useHideModal({
    onHide: () => {
      formDataSet(initialFormState)
      setFormErrors(initialFormState)
      typeSet(DocumentType.NULL)
      hasSubmitted.current = false
      setIsValidLink(true)
    },
    modalId: ModalFormId.ADD_DOCUMENT,
  })

  const onHideHandler = () => {
    setSpFilesSelected([])
    setGdFileSelected?.([])
    setCfFilesSelected([])
    hide()
  }

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      try {
        e.preventDefault()
        if (!policy) {
          return
        }
        if (!formData.caption && !hideCaption) {
          setFormErrors((formErrors) => ({
            ...formErrors,
            caption: 'Caption is required',
          }))
          return
        }
        if (
          !formData.body &&
          spFileSelected.length === 0 &&
          (gdFileSelected?.length || 0) === 0 &&
          cfFileSelected.length === 0
        ) {
          setFormErrors((formErrors) => ({
            ...formErrors,
            body: 'Document is required',
          }))
          return
        }

        // SharePoint file picker
        if (spFileSelected.length > 0) {
          setThrobberState({
            isShown: true,
            Display: <Spinner color="primary" size="xl" />,
          })
          await onDMSubmit({
            caption: formData.caption,
          })
          setSpFilesSelected([])
          hide()
          await mutatePolicyDocs()
          return
        }

        // Google Drive picker
        if ((gdFileSelected ?? []).length > 0) {
          setThrobberState({
            isShown: true,
            Display: <Spinner color="primary" size="xl" />,
          })
          await onGdSubmit({
            caption: formData.caption,
          })
          setGdFileSelected?.([])
          hide()
          await mutatePolicyDocs()
          return
        }

        // Confluence picker
        if (cfFileSelected.length > 0) {
          const node = cfFileSelected[0]
          setThrobberState({
            isShown: true,
            Display: <Spinner color="primary" size="xl" />,
          })

          await onCfSubmit({
            caption: formData.caption,
            node,
          })

          setCfFilesSelected([])
          hide()
          await mutatePolicyDocs()
          return
        }

        if (type === DocumentType.SHAREPOINTLINK) {
          const isValid = isSharepointurlValid(formData.body.toString())
          setIsValidLink(isValid)
          if (!isValid) {
            setFormErrors((formErrors) => ({
              ...formErrors,
              body: 'Please enter a valid SharePoint URL',
            }))
            return
          }
          setThrobberState({
            isShown: true,
            Display: <Spinner color="primary" size="xl" />,
          })
          await runSharepointLinker(
            formData.body.toString(),
            DOCUMENT_TYPE.POLICYDOC,
            false,
            formData.caption,
            [policyId],
          )
          hide()
          await mutatePolicyDocs()
          return
        }

        // Trigger URL link validation only after Submit
        if (type === DocumentType.LINK) {
          const isValid: boolean =
            isString(formData.body) && isValidUrl(formData.body)
          hasSubmitted.current = true
          setIsValidLink(isValid)
          if (!isValid) {
            return
          }
        }

        hide()
        setThrobberState({
          isShown: true,
          Display: <Spinner color="primary" size="xl" />,
        })

        // Add evidence (upload body)
        const { mime, caption, body } = formData
        const bodyBytes = new Uint8Array(
          body instanceof Blob
            ? // Convert blob to arrayBuffer (aka byte array) first
              await new Response(body).arrayBuffer()
            : // Convert string into utf8 encoded codes
              Array.from(utf8.encode(body.trim())).map((s) => s.charCodeAt(0)),
        )
        const documentMsg = new DocumentMsg()
          .setSubjectmodeltype(MODEL_TYPE.POLICY)
          .setSubjectmodelid(policy.getModelId())
          .setDoctype(DOCUMENT_TYPE.POLICYDOC)
          .setMime(mime)
          .setBody(bodyBytes)
          .setCaption(caption.trim())
          // since the user is not using one of our templates, we set isCustomized to true
          .setIsCustomized(new BoolValue().setValue(true))
        if (body instanceof File) {
          documentMsg.setFileName(body.name)
        }

        await attachmentClient.addDocument(
          new AddDocumentRequest().setDocument(documentMsg),
        )
        await mutatePolicyDocs()
      } catch (err) {
        log.error('error in add document form', err)
        showInfoToast(ToastPrompts.POLICY_DOC_UPLOAD_FAILED)
        if (
          setSpFilesSelected ||
          setGdFileSelected ||
          setCfFilesSelected ||
          type === DocumentType.SHAREPOINTLINK
        ) {
          setSpFilesSelected([])
          setGdFileSelected?.([])
          setCfFilesSelected([])
          showAlerDialog({
            title: 'Unable to Add Policy Document',
            body: 'We couldn’t upload your document. Please check your permissions or try again. If the problem continues, contact your administrator for help.',
          })
        }
        if ((gdFileSelected ?? []).length > 0) {
          setGdFileSelected?.([])
          showAlerDialog({
            title: 'Unable to Add Policy Document',
            body: 'We couldn’t upload your document. Please check your permissions or try again. If the problem continues, contact your administrator for help.',
          })
        }
      } finally {
        setThrobberState({
          isShown: false,
          Display: <></>,
        })
      }
    },
    [
      policy,
      type,
      hide,
      setThrobberState,
      formData,
      attachmentClient,
      mutatePolicyDocs,
      spFileSelected,
      onDMSubmit,
      setSpFilesSelected,
      gdFileSelected,
      onGdSubmit,
      setGdFileSelected,
      cfFileSelected,
      onCfSubmit,
      setCfFilesSelected,
      hideCaption,
      showAlerDialog,
      isSharepointurlValid,
      policyId,
      runSharepointLinker,
    ],
  )

  const onChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const newVal = e.target.value
    if (newVal.trim().length > 0) {
      setFormErrors((formErrors) => ({
        ...formErrors,
        caption: '',
      }))
    }
    formDataSet((formData) => ({
      ...formData,
      caption: newVal,
    }))
  }

  if (!policy) {
    return <></>
  }

  return (
    <ModalForm
      show={show}
      hide={onHideHandler}
      formId={ModalFormId.ADD_DOCUMENT}
      title="Add Document for Policy:"
      description={`"${policy.getName()}"`}
      openOnDrag={openOnDrag}
    >
      <form
        id={ModalFormId.ADD_DOCUMENT}
        onSubmit={onSubmit}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {hideCaption ? (
          <P $fontSize={14} $mb={30}>
            Captions will automatically be set to the file names.
          </P>
        ) : (
          <DerivedTextInput
            label="Caption"
            name="caption"
            initVal={formData.caption}
            placeholder="Add Caption for Document"
            form={ModalFormId.ADD_DOCUMENT}
            setFormData={
              formDataSet as Dispatch<SetStateAction<{ [key: string]: string }>>
            }
            isValid={!formErrors.caption}
            errorMessage={formErrors.caption}
            customOnChange={onChange}
          />
        )}
        <DocumentDetailsContainer>
          <Label>Document Details</Label>
          {type === DocumentType.LINK && (
            <P $color={palette.orange['900']} $isBold>
              Currently Trustero AI can only evaluate evidence that is stored in
              the platform. If you store your evidence somewhere else that you
              link to, Trustero AI will not be able to evaluate it.
            </P>
          )}
          {spFileSelected.length > 0 ||
          (gdFileSelected?.length || 0) > 0 ||
          cfFileSelected.length > 0 ? (
            <TextButton
              onClick={() => {
                setSpFilesSelected([])
                setGdFileSelected?.([])
                setCfFilesSelected([])
              }}
            >
              Clear
            </TextButton>
          ) : (
            <DocumentTypeSelector
              {...{
                setFormData: formDataSet,
                type,
                setType: typeSet,
                hide,
                policyId,
              }}
            />
          )}
          <PolicyDocumentManagementFiles />
          <DocumentFile {...{ formData, type }} />
          <DocumentLink
            {...{
              formData,
              setFormData: formDataSet,
              isValidLink,
              formId: ModalFormId.ADD_DOCUMENT,
              type,
            }}
          />
          <StyledInputError showError={!!formErrors.body}>
            {formErrors.body as string}
          </StyledInputError>
        </DocumentDetailsContainer>
      </form>
    </ModalForm>
  )
}
